import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import emailValidationService from '../../../utils/emailValidationService';

const HistoryTable = ({ filters, onFiltersChange }) => {
  const navigate = useNavigate();
  const [validations, setValidations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalResults: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false
  });

  useEffect(() => {
    const loadValidations = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const result = await emailValidationService.getValidationHistory({
          ...filters,
          page: pagination.currentPage,
          limit: pagination.limit
        });
        
        if (result.success) {
          setValidations(result.data.validations);
          setPagination(result.data.pagination);
        } else {
          setError(result.error);
        }
      } catch (err) {
        setError('Failed to load validation history');
      } finally {
        setLoading(false);
      }
    };

    loadValidations();
  }, [filters, pagination.currentPage]);

  const getStatusBadge = (status) => {
    const badges = {
      completed: { color: 'text-success bg-success/10', icon: 'CheckCircle', text: 'Completed' },
      processing: { color: 'text-primary bg-primary/10', icon: 'Loader', text: 'Processing' },
      failed: { color: 'text-error bg-error/10', icon: 'XCircle', text: 'Failed' },
      pending: { color: 'text-warning bg-warning/10', icon: 'Clock', text: 'Pending' },
      cancelled: { color: 'text-text-secondary bg-gray-100', icon: 'X', text: 'Cancelled' }
    };

    const badge = badges[status] || badges.pending;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon name={badge.icon} size={12} className="mr-1" />
        {badge.text}
      </span>
    );
  };

  const getValidationTypeIcon = (type) => {
    switch (type) {
      case 'single':
        return 'Mail';
      case 'bulk':
        return 'Users';
      case 'file_upload':
        return 'FileText';
      default:
        return 'Mail';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleViewResults = (validationId) => {
    navigate(`/validation-history/results/${validationId}`);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  if (loading) {
    return (
      <div className="bg-surface border border-border rounded-lg p-6">
        <div className="flex items-center justify-center h-48">
          <Icon name="Loader" size={24} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-surface border border-border rounded-lg p-6">
        <div className="flex items-center justify-center h-48">
          <div className="text-center">
            <Icon name="AlertCircle" size={24} className="text-error mx-auto mb-2" />
            <p className="text-error">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-border">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                File/Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Results
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-surface divide-y divide-border">
            {validations.length > 0 ? (
              validations.map((validation) => (
                <tr key={validation.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <Icon name={getValidationTypeIcon(validation.validation_type)} size={16} className="text-primary" />
                      <span className="text-sm font-medium text-text-primary capitalize">
                        {validation.validation_type.replace('_', ' ')}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-text-primary">
                      {validation.file_name || `${validation.validation_type} Validation`}
                    </div>
                    {validation.file_format && (
                      <div className="text-xs text-text-secondary uppercase">
                        {validation.file_format}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(validation.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-text-primary">
                      {validation.processed_emails || 0} / {validation.total_emails || 0}
                    </div>
                    <div className="text-xs text-text-secondary">
                      {validation.total_emails > 0 ? Math.round((validation.processed_emails / validation.total_emails) * 100) : 0}% complete
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                    {formatDate(validation.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewResults(validation.id)}
                      iconName="Eye"
                      iconSize={14}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="px-6 py-12 text-center">
                  <Icon name="Inbox" size={24} className="text-text-secondary mx-auto mb-2" />
                  <p className="text-text-secondary">No validation history found</p>
                  <p className="text-sm text-text-secondary">
                    Start validating emails to see your history here
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-3 border-t border-border">
          <div className="text-sm text-text-secondary">
            Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to {Math.min(pagination.currentPage * pagination.limit, pagination.totalResults)} of {pagination.totalResults} results
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={!pagination.hasPrevPage}
              iconName="ChevronLeft"
              iconSize={16}
            >
              Previous
            </Button>
            
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-1 text-sm rounded ${
                      pagination.currentPage === page
                        ? 'bg-primary text-white' :'text-text-secondary hover:text-primary'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasNextPage}
              iconName="ChevronRight"
              iconSize={16}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoryTable;